import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Leaf, Loader2, Mail, Lock, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

export default function AuthModal() {
  const { authOpen, authMode, closeAuth, openAuth, register, signin } = useAuth();
  const [mode, setMode] = useState(authMode);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setMode(authMode);
  }, [authMode, authOpen]);

  useEffect(() => {
    if (!authOpen) {
      setName(''); setEmail(''); setPassword(''); setLoading(false);
    }
  }, [authOpen]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'register') {
        await register({ name, email, password });
        toast.success('Welcome to Your Herbal Companion!');
      } else {
        await signin({ email, password });
        toast.success('Welcome back!');
      }
      closeAuth();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const isRegister = mode === 'register';

  return (
    <Dialog open={authOpen} onOpenChange={(o) => (o ? null : closeAuth())}>
      <DialogContent className="bg-botanica-panel border-botanica-card max-w-md rounded-3xl p-0 overflow-hidden">
        <div className="bg-botanica-greenLight px-7 pt-7 pb-6 text-center">
          <span className="w-12 h-12 rounded-2xl bg-botanica-panel flex items-center justify-center mx-auto mb-3">
            <Leaf className="w-6 h-6 text-botanica-green" />
          </span>
          <h2 className="font-serif text-2xl text-botanica-ink">
            {isRegister ? 'Create your account' : 'Welcome back'}
          </h2>
          <p className="text-sm text-botanica-greenDark/80 mt-1">
            {isRegister ? 'Join free to save your kit and chat with Sage.' : 'Sign in to your herbal companion.'}
          </p>
        </div>

        <form onSubmit={submit} className="px-7 py-6 space-y-4">
          {isRegister && (
            <div className="relative">
              <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-botanica-muted" />
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="w-full pl-10 pr-4 py-3 rounded-xl bg-botanica-card/60 border border-botanica-card text-sm text-botanica-ink placeholder:text-botanica-muted focus:outline-none focus:ring-2 focus:ring-botanica-green/40"
              />
            </div>
          )}
          <div className="relative">
            <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-botanica-muted" />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
              className="w-full pl-10 pr-4 py-3 rounded-xl bg-botanica-card/60 border border-botanica-card text-sm text-botanica-ink placeholder:text-botanica-muted focus:outline-none focus:ring-2 focus:ring-botanica-green/40"
            />
          </div>
          <div className="relative">
            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-botanica-muted" />
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={isRegister ? 'Create a password (min 6 chars)' : 'Password'}
              className="w-full pl-10 pr-4 py-3 rounded-xl bg-botanica-card/60 border border-botanica-card text-sm text-botanica-ink placeholder:text-botanica-muted focus:outline-none focus:ring-2 focus:ring-botanica-green/40"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-botanica-green text-white font-medium hover:bg-botanica-greenDark transition-colors disabled:opacity-70"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {isRegister ? 'Create free account' : 'Sign in'}
          </button>

          <p className="text-center text-sm text-botanica-muted">
            {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button
              type="button"
              onClick={() => openAuth(isRegister ? 'login' : 'register')}
              className="text-botanica-green font-medium hover:underline"
            >
              {isRegister ? 'Sign in' : 'Create one'}
            </button>
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
